import {
  Box, Flex, Text, VStack,
} from '@chakra-ui/react';
import { IBasket } from '../../../lib/graphql';
import formatMoney from '../../../utils/money';
import { previewTotal, previewChargeTotal } from '../../../utils/basketTotals';
import PromoCodeRow from './PromoCodeRow';

interface BasketSummaryProps {
  basket: IBasket;
  variant?: 'mobile' | 'desktop';
}

// Summary row component - moved outside to avoid re-creation on each render
const SummaryRow = ({
  label, value, isTotal = false, color = 'gray.600', isDesktop = false,
}: {
  label: string;
  value: number;
  isTotal?: boolean;
  color?: string;
  isDesktop?: boolean;
}) => (
  <Flex justify="space-between">
    <Text
      fontSize={isDesktop ? 'md' : 'sm'}
      color={isTotal ? 'gray.900' : color}
      fontWeight={isTotal ? 'bold' : 'normal'}
    >
      {label}
    </Text>
    <Text
      fontSize={isDesktop ? 'md' : 'sm'}
      fontWeight={isTotal ? 'bold' : 'medium'}
      color={isTotal ? 'gray.900' : color}
    >
      {formatMoney(value)}
    </Text>
  </Flex>
);

// Payment breakdown component - moved outside to avoid re-creation on each render
// Receives already-computed display values (fee excluded) rather than reading the
// raw basket, so the preview total does not include the checkout-only processing fee.
const PaymentBreakdown = ({
  total, chargeTotal, payLater, isDesktop,
}: {
  total: number; chargeTotal: number; payLater: number; isDesktop: boolean;
}) => {
  if (payLater > 0) {
    return (
      <VStack spacing={isDesktop ? 3 : 2}>
        <SummaryRow label="Total" value={total} isTotal isDesktop={isDesktop} />
        <Flex
          justify="space-between"
          w="full"
          bg="blue.50"
          p={isDesktop ? 3 : 2}
          borderRadius="md"
        >
          <Text
            fontSize={isDesktop ? 'md' : 'sm'}
            color="blue.700"
            fontWeight="medium"
          >
            Pay now
          </Text>
          <Text
            fontSize={isDesktop ? 'md' : 'sm'}
            fontWeight="bold"
            color="blue.700"
          >
            {formatMoney(chargeTotal)}
          </Text>
        </Flex>
        <Flex
          justify="space-between"
          w="full"
          bg="pink.50"
          p={isDesktop ? 3 : 2}
          borderRadius="md"
        >
          <Text
            fontSize={isDesktop ? 'md' : 'sm'}
            color="pink.700"
            fontWeight="medium"
          >
            Pay later
          </Text>
          <Text
            fontSize={isDesktop ? 'md' : 'sm'}
            fontWeight="bold"
            color="pink.700"
          >
            {formatMoney(payLater)}
          </Text>
        </Flex>
      </VStack>
    );
  }

  return (
    <Flex
      justify="space-between"
      bg="blue.50"
      p={isDesktop ? 4 : 3}
      borderRadius="md"
    >
      <Text
        fontSize={isDesktop ? 'lg' : 'md'}
        fontWeight="bold"
        color="blue.700"
      >
        Total
      </Text>
      <Text
        fontSize={isDesktop ? 'lg' : 'md'}
        fontWeight="bold"
        color="blue.700"
      >
        {formatMoney(total)}
      </Text>
    </Flex>
  );
};

/**
 * Reusable basket summary component
 * Displays subtotal, discounts, promo codes, credits, tax, and total
 * Supports both mobile and desktop layouts
 */
const BasketSummary = ({ basket, variant = 'mobile' }: BasketSummaryProps): JSX.Element => {
  const isDesktop = variant === 'desktop';

  // The £1 processing fee is folded into basket.total / basket.chargeTotal by the
  // backend, but it is only shown at checkout — never in this basket preview.
  // Subtract it back out so the preview stays item/discount/credit based, and do
  // not render a fee line here. Shared with basketFooter so the two agree.
  const displayTotal = previewTotal(basket);
  const displayChargeTotal = previewChargeTotal(basket);

  return (
    <Box
      bg="white"
      p={isDesktop ? 6 : 4}
      borderRadius={isDesktop ? 'lg' : 'md'}
      borderWidth={1}
      shadow={isDesktop ? 'sm' : undefined}
      borderColor="gray.200"
    >
      {isDesktop && (
        <Box bg="gray.50" px={6} py={4} borderBottom="1px solid" borderColor="gray.200" mb={4} mx={-6} mt={-6}>
          <Text fontSize="lg" fontWeight="semibold" color="gray.700">Order Summary</Text>
        </Box>
      )}

      <VStack spacing={isDesktop ? 4 : 2} align="stretch">
        <SummaryRow label="Subtotal" value={basket.subTotal} isDesktop={isDesktop} />

        {basket.discountTotal > 0 && (
          <SummaryRow
            label="Discount"
            value={basket.discountTotal}
            color="green.600"
            isDesktop={isDesktop}
          />
        )}

        {basket.promoCodeDiscountValue > 0 && basket.appliedBasketPromoCode && (
          <PromoCodeRow
            code={basket.appliedBasketPromoCode.code}
            value={basket.promoCodeDiscountValue}
          />
        )}

        {basket.items?.filter((item) => item.appliedItemPromoCode && item.promoCodeDiscountValue > 0).map((item, index) => (
          <PromoCodeRow
            key={`item-promo-${item.appliedItemPromoCode.code}-${item.id || index}`}
            code={item.appliedItemPromoCode.code}
            value={item.promoCodeDiscountValue}
          />
        ))}

        {basket.creditTotal > 0 && (
          <SummaryRow
            label="Account Credit"
            value={basket.creditTotal}
            color="green.600"
            isDesktop={isDesktop}
          />
        )}

        {basket.tax > 0 && (
          <SummaryRow label="VAT (20%)" value={basket.tax} isDesktop={isDesktop} />
        )}

        <Box borderTop="2px solid" borderColor="gray.200" pt={isDesktop ? 4 : 2}>
          <PaymentBreakdown
            total={displayTotal}
            chargeTotal={displayChargeTotal}
            payLater={basket.payLater ?? 0}
            isDesktop={isDesktop}
          />
        </Box>
      </VStack>
    </Box>
  );
};

export default BasketSummary;
